import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, Area, AreaChart
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ChartProps {
  data?: any[];
  title: string;
  description?: string;
  type: 'bar' | 'pie' | 'radar' | 'line' | 'area';
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export const EnhancedChart: React.FC<ChartProps> = ({ 
  data = [], 
  title, 
  description, 
  type 
}) => {
  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={data}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" />
              <PolarRadiusAxis />
              <Radar name="Score" dataKey="value" stroke="#0088FE" fill="#0088FE" fillOpacity={0.6} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#0088FE" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#0088FE" fill="#0088FE" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      default:
        return <div className="flex items-center justify-center h-[300px] text-muted-foreground">Chart type not supported</div>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {title}
          <Badge variant="outline">{data.length} items</Badge>
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {data.length > 0 ? renderChart() : (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No data available for visualization
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Specialized charts for specific data types
export const FormulationComparisonChart: React.FC<{ formulations: any[] }> = ({ formulations }) => {
  const chartData = formulations.map((formulation, index) => ({
    name: `Formula ${index + 1}`,
    costReduction: formulation.cost_reduction_pct || 0,
    similarity: formulation.similarity_score || 0,
    nutritionScore: formulation.nutrition_improvement?.length || 0,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Formulation Performance Comparison</CardTitle>
        <CardDescription>Cost reduction vs similarity scores across formulations</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="costReduction" fill="#0088FE" name="Cost Reduction %" />
            <Bar dataKey="similarity" fill="#00C49F" name="Similarity Score" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export const SensoryRadarChart: React.FC<{ evaluation: any }> = ({ evaluation }) => {
  if (!evaluation?.sensory) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sensory Evaluation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No sensory data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const radarData = [
    { subject: 'Taste', value: evaluation.sensory.taste || 0 },
    { subject: 'Texture', value: evaluation.sensory.texture || 0 },
    { subject: 'Appearance', value: evaluation.sensory.appearance || 0 },
    { subject: 'Overall', value: evaluation.sensory.overall || 0 },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sensory Evaluation Profile</CardTitle>
        <CardDescription>Comprehensive sensory analysis scores (0-10 scale)</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" />
            <PolarRadiusAxis domain={[0, 10]} />
            <Radar 
              name="Sensory Score" 
              dataKey="value" 
              stroke="#0088FE" 
              fill="#0088FE" 
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export const MarketTrendChart: React.FC<{ marketData: any }> = ({ marketData }) => {
  // Mock trend data - in real implementation, this would come from API
  const trendData = [
    { month: 'Jan', marketSize: 150, growth: 2.1 },
    { month: 'Feb', marketSize: 155, growth: 3.3 },
    { month: 'Mar', marketSize: 160, growth: 3.2 },
    { month: 'Apr', marketSize: 165, growth: 3.1 },
    { month: 'May', marketSize: 172, growth: 4.2 },
    { month: 'Jun', marketSize: 180, growth: 4.7 },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Market Growth Trends</CardTitle>
        <CardDescription>6-month market size and growth rate progression</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={trendData}>
            <defs>
              <linearGradient id="colorMarketSize" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0088FE" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#0088FE" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Area 
              type="monotone" 
              dataKey="marketSize" 
              stroke="#0088FE" 
              fillOpacity={1} 
              fill="url(#colorMarketSize)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}; 